import { ref, computed, onMounted, onUnmounted } from "vue";

/**
 * useViewport - Reactive viewport size detection composable
 *
 * Provides reactive viewport width and screen size breakpoint detection.
 * Automatically updates when the window is resized.
 *
 * Breakpoint definitions (based on Tailwind defaults):
 * - Small screens: < 1024px (xs, sm, md)
 * - Large screens: >= 1024px (lg, xl, 2xl+)
 *
 * @returns {Object} Viewport state object
 * SSR note:
 * - During SSR and before client mount, viewport state stays neutral to avoid
 *   rendering different DOM trees between server and client hydration.
 *
 * @returns {Ref<number | null>} viewportWidth - Current viewport width in pixels after mount
 * @returns {ComputedRef<boolean>} isHydrated - True after client mount initializes viewport state
 * @returns {ComputedRef<boolean>} isSmallScreen - True for screens < 1024px after hydration
 * @returns {ComputedRef<boolean>} isLargeScreen - True for screens >= 1024px after hydration
 */
export const useViewport = () => {
  const viewportWidth = ref<number | null>(null);

  // Tailwind lg breakpoint
  const LARGE_SCREEN_BREAKPOINT = 1024;

  const isHydrated = computed(() => viewportWidth.value !== null);

  const isSmallScreen = computed(
    () =>
      viewportWidth.value !== null &&
      viewportWidth.value < LARGE_SCREEN_BREAKPOINT
  );
  const isLargeScreen = computed(
    () =>
      viewportWidth.value !== null &&
      viewportWidth.value >= LARGE_SCREEN_BREAKPOINT
  );

  const updateViewportWidth = () => {
    if (typeof window !== "undefined") {
      viewportWidth.value = window.innerWidth;
    }
  };

  onMounted(() => {
    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth);
  });

  onUnmounted(() => {
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", updateViewportWidth);
    }
  });

  return {
    viewportWidth,
    isHydrated,
    isSmallScreen,
    isLargeScreen,
  };
};
